import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Linkedin, Instagram, Facebook, Youtube, Loader2,
  CheckCircle2, AlertCircle, KeyRound, Save, Trash2, Eye, EyeOff, Power
} from 'lucide-react';
import { toast } from 'sonner';

const PLATFORM_LABELS = {
  linkedin:  { label: 'LinkedIn',  icon: Linkedin,  color: 'text-sky-600' },
  instagram: { label: 'Instagram', icon: Instagram, color: 'text-pink-600' },
  facebook:  { label: 'Facebook',  icon: Facebook,  color: 'text-blue-600' },
  youtube:   { label: 'YouTube',   icon: Youtube,   color: 'text-red-600' },
  tiktok:    { label: 'TikTok',    icon: () => <span className="text-xs font-bold leading-none">TT</span>, color: 'text-zinc-700' },
  bluesky:   { label: 'Bluesky',   icon: () => <span className="text-xs font-bold leading-none">BS</span>, color: 'text-sky-500' },
};

export const PlatformCredentialsManager = () => {
  const { api } = useAuth();
  const [rows, setRows] = useState([]);
  const [toggles, setToggles] = useState([]); // [{platform, enabled, updated_at}]
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({}); // { platform: {client_id, client_secret, redirect_uri} }
  const [saving, setSaving] = useState(null);
  const [verifying, setVerifying] = useState(null);
  const [togglingPlatform, setTogglingPlatform] = useState(null);
  const [showSecret, setShowSecret] = useState({}); // { platform: bool }

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const [credRes, togRes] = await Promise.all([
        api.get('/admin/platform-credentials'),
        api.get('/admin/platform-toggles'),
      ]);
      setRows(credRes.data.platforms || []);
      setToggles(togRes.data.toggles || []);
      const newDrafts = {};
      (credRes.data.platforms || []).forEach((r) => {
        newDrafts[r.platform] = {
          client_id: r.client_id || '',
          client_secret: '',
          redirect_uri: r.redirect_uri || '',
        };
      });
      setDrafts(newDrafts);
    } catch {
      toast.error('Failed to load platform credentials');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const onChange = (platform, key, value) => {
    setDrafts((d) => ({ ...d, [platform]: { ...d[platform], [key]: value } }));
  };

  const handleSave = async (platform) => {
    const draft = drafts[platform] || {};
    if (!draft.client_id || !draft.client_secret) {
      toast.error('Client ID and Client Secret are required');
      return;
    }
    setSaving(platform);
    try {
      await api.put(`/admin/platform-credentials/${platform}`, draft);
      toast.success(`${PLATFORM_LABELS[platform]?.label || platform} credentials saved`);
      fetchRows();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(null);
    }
  };

  const handleVerify = async (platform) => {
    setVerifying(platform);
    try {
      const res = await api.post(`/admin/platform-credentials/${platform}/verify`);
      if (res.data.ok) toast.success(`${platform} credentials look valid`);
      else toast.error(`Issues: ${res.data.issues.join(', ')}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verify failed');
    } finally {
      setVerifying(null);
    }
  };

  const handleDelete = async (platform) => {
    if (!window.confirm(`Remove ${platform} credentials?`)) return;
    try {
      await api.delete(`/admin/platform-credentials/${platform}`);
      toast.success('Removed');
      fetchRows();
    } catch {
      toast.error('Delete failed');
    }
  };

  const handleToggle = async (platform, status) => {
    setTogglingPlatform(platform);
    try {
      await api.put(`/admin/platform-toggles/${platform}`, { status });
      setToggles((prev) =>
        prev.map((t) => (t.platform === platform ? { ...t, status, enabled: status === 'enabled', updated_at: new Date().toISOString() } : t))
      );
      const labelMap = { enabled: 'Enabled', pending: 'Set to Pending Approval', disabled: 'Disabled' };
      toast.success(`${PLATFORM_LABELS[platform]?.label || platform}: ${labelMap[status] || status}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Toggle failed');
    } finally {
      setTogglingPlatform(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="platform-credentials-manager">
      <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-50 border border-amber-200">
        <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
        <div className="text-sm text-amber-900">
          <p className="font-medium">Each platform requires its own Developer App + approval.</p>
          <p className="text-xs mt-1 text-amber-800">
            Add Client ID, Client Secret and (optionally) Redirect URL once approved. The default Redirect URL points to <code className="text-xs bg-white px-1 py-0.5 rounded">/connections/callback/&lt;platform&gt;</code> on this app.
          </p>
        </div>
      </div>

      {/* Global on/off toggles — disable any platform that isn't approved yet,
          mark it Pending Approval, or temporarily turn off a network without
          losing credentials. */}
      <Card data-testid="platform-toggles-panel">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Power className="w-4 h-4 text-indigo-600" />
            Platform availability
          </CardTitle>
          <CardDescription className="text-xs">
            <strong>Enabled</strong> — visible to everyone. <strong>Pending Approval</strong> — greyed-out card visible to users with a badge; only admins can connect &amp; test (use while your developer-app is under review). <strong>Disabled</strong> — hidden from users entirely; admins still see it for debugging.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {toggles.map((t) => {
            const meta = PLATFORM_LABELS[t.platform] || { label: t.platform, icon: KeyRound, color: 'text-zinc-700' };
            const Icon = meta.icon;
            const status = t.status || (t.enabled ? 'enabled' : 'disabled');
            const subText = {
              enabled: 'Available to all users',
              pending: 'Greyed-out for users — admin can test',
              disabled: 'Hidden from users — admin can debug',
            }[status];
            return (
              <div
                key={t.platform}
                className="flex items-center justify-between py-2 border-b border-zinc-100 last:border-0"
                data-testid={`toggle-row-${t.platform}`}
              >
                <div className="flex items-center gap-3">
                  <Icon className={`w-5 h-5 ${meta.color}`} />
                  <div>
                    <p className="text-sm font-medium text-zinc-900">{meta.label}</p>
                    <p className="text-xs text-zinc-500">{subText}</p>
                  </div>
                </div>
                <Select
                  value={status}
                  disabled={togglingPlatform === t.platform}
                  onValueChange={(v) => handleToggle(t.platform, v)}
                >
                  <SelectTrigger className="w-44" data-testid={`status-select-${t.platform}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="enabled" data-testid={`status-enabled-${t.platform}`}>
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-emerald-500" /> Enabled
                      </span>
                    </SelectItem>
                    <SelectItem value="pending" data-testid={`status-pending-${t.platform}`}>
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-amber-500" /> Pending Approval
                      </span>
                    </SelectItem>
                    <SelectItem value="disabled" data-testid={`status-disabled-${t.platform}`}>
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-zinc-400" /> Disabled
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            );
          })}
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-2 gap-4">
        {rows.map((row) => {
          const meta = PLATFORM_LABELS[row.platform] || { label: row.platform, icon: KeyRound, color: 'text-zinc-700' };
          const Icon = meta.icon;
          const draft = drafts[row.platform] || {};
          return (
            <Card
              key={row.platform}
              className="border-zinc-200"
              data-testid={`platform-row-${row.platform}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Icon className={`w-5 h-5 ${meta.color}`} />
                    {meta.label}
                  </CardTitle>
                  {row.is_configured ? (
                    <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Configured
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-zinc-100 text-zinc-700">Not set</Badge>
                  )}
                </div>
                {row.updated_at && (
                  <CardDescription className="text-xs">
                    Updated {new Date(row.updated_at).toLocaleString()}
                  </CardDescription>
                )}
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Client ID</Label>
                  <Input
                    placeholder="e.g. 78abc123def456"
                    value={draft.client_id || ''}
                    onChange={(e) => onChange(row.platform, 'client_id', e.target.value)}
                    data-testid={`client-id-${row.platform}`}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Client Secret {row.is_configured ? <span className="text-zinc-400">(leave blank to keep current)</span> : null}</Label>
                  <div className="relative">
                    <Input
                      type={showSecret[row.platform] ? 'text' : 'password'}
                      placeholder={row.client_secret_masked || 'Paste secret'}
                      value={draft.client_secret || ''}
                      onChange={(e) => onChange(row.platform, 'client_secret', e.target.value)}
                      className="pr-9"
                      data-testid={`client-secret-${row.platform}`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowSecret((s) => ({ ...s, [row.platform]: !s[row.platform] }))}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                    >
                      {showSecret[row.platform] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Redirect URL (allowlist this in your dev app)</Label>
                  <Input
                    placeholder={`${window.location.origin}/connections/callback/${row.platform}`}
                    value={draft.redirect_uri || ''}
                    onChange={(e) => onChange(row.platform, 'redirect_uri', e.target.value)}
                    data-testid={`redirect-uri-${row.platform}`}
                  />
                </div>
                <div className="flex gap-2 pt-1">
                  <Button
                    size="sm"
                    className="bg-indigo-600 hover:bg-indigo-700"
                    onClick={() => handleSave(row.platform)}
                    disabled={saving === row.platform}
                    data-testid={`save-${row.platform}`}
                  >
                    {saving === row.platform ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
                    Save
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleVerify(row.platform)}
                    disabled={!row.is_configured || verifying === row.platform}
                    data-testid={`verify-${row.platform}`}
                  >
                    {verifying === row.platform ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />}
                    Verify
                  </Button>
                  {row.is_configured && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 border-red-200 hover:bg-red-50 ml-auto"
                      onClick={() => handleDelete(row.platform)}
                      data-testid={`delete-${row.platform}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
